// `search-form/page/generic-tags-input-with-tooltip.tpl.html` — the tag entry widget the
// criteria builder uses for skills, positions, companies and locations.
//
// It is `ng-tags-input` with a custom template, so the MARKUP is upstream's and the behaviour
// is the vendor's. What is ported here is the markup and the parts of the behaviour the screen
// depends on; what is not is the vendor's own machinery:
//
//   - `ti-autosize` grows the input to fit what is typed;
//   - `ti-transclude-append` hangs the `<auto-complete>` dropdown off the host;
//   - `add-from-autocomplete-only="true"` is the rule that matters and IS ported: a tag may
//     only be created by choosing a suggestion, never by typing free text and pressing enter.
//     Without it a user can add "jva" and get a search that matches nothing, which is exactly
//     what the "Please fill in skills from the list" error beside it exists to prevent.
//
// ⚠️ THE `remove-button` TEXT COMES FROM `options.removeTagSymbol`, which is the vendor's
// default `×` (U+00D7), not the letter x and not `&times;` rendered as markup. It is bound with
// `ng-bind`, so it is text content — a `<i class="fa-times">` here would be a different glyph
// at a different size.
import { createElement, useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'

export interface Tag {
  /** `display-property="text"` — every caller in the builder uses `text`. */
  text: string
  /** Renders the flame marker before the label. */
  inDemand?: boolean
  /**
   * ⚠️ THE WIRE OBJECT THE TAG CAME FROM, CARRIED SO IT CAN GO BACK.
   *
   * A location tag is not a string on the server: `IExtensionLocation` is
   * `{ id, city, region_state, country, lat, lon, distance, unit }`, and `saveAlert` sends
   * those objects back. A load that keeps only `text` round-trips a city into a bare label the
   * server cannot resolve — so the mapper stores the original here and the save reads it.
   * Everything that is genuinely a string (skills, keywords, company names) leaves it unset.
   */
  source?: Readonly<Record<string, unknown>>
  /**
   * `item.addNew` — the row that offers the user's own words. The title panel quotes it and
   * the plain list does not, so it has to reach the renderer.
   */
  addNew?: boolean
}

/** What a custom panel is handed; enough to render and to drive selection. */
export interface SuggestionPanelView {
  items: readonly Tag[]
  /** The index into `items` that is currently selected. */
  highlighted: number
  query: string
  onPick: (tag: Tag) => void
  onHighlight: (index: number) => void
  /** The same `<em>`-wrapping the default list uses. */
  highlight: (text: string) => ReactNode
}

export interface TagsInputProps {
  tags: readonly Tag[]
  placeholder?: string
  name?: string
  className?: string
  /** `tags-input-indicator` — a magnifier shown while the field is empty. */
  indicator?: boolean
  indicatorClassName?: string
  /** The suggestion list; a tag can only be added from it unless `freeText`. */
  suggestions?: readonly Tag[]
  /**
   * Upstream omits `add-from-autocomplete-only` on two of the builder's tag fields: KEYWORDS,
   * where free text is the whole point, and COMPANY NAME, where a user may search for a company
   * Woo has never indexed. Everywhere else the attribute is present and Enter must take a
   * suggestion.
   *
   * ⚠️ A DEFAULT OF `true` HERE WOULD BE THE WRONG WAY ROUND. The attribute is opt-IN upstream,
   * but the majority of call sites opt in — so the safe default is the strict one, and the two
   * free-text fields say so explicitly rather than the other seven repeating themselves.
   */
  freeText?: boolean
  onQueryChange?: (query: string) => void
  /**
   * `<auto-complete tags-input-loader-source="…">` — the suggestion source.
   *
   * ⚠️ THE LIST IS THE AUTOCOMPLETE DIRECTIVE'S OWN, NOT THE CALLER'S. Upstream nests a
   * second directive inside `<tags-input>` which owns the query, the debounce and the
   * `.autocomplete` panel; this port had a `suggestions` prop that Enter read and NOTHING
   * rendered, so every field on both builders looked dead. Passing a loader puts the panel
   * back where it belongs — in here, once, rather than in nine call sites.
   *
   * The defaults below are the directive's (`ng-tags-input.js:622`), overridden per field by
   * the attributes upstream actually writes: `min-length="1"` and `debounce-delay="500"` on
   * the title fields, `max-results-to-show="20"`.
   */
  loadSuggestions?: (query: string) => Promise<readonly Tag[]>
  /** `min-length` — the directive's default is 3; the builder's fields use 1. */
  minLength?: number
  /**
   * `load-on-empty` / `load-on-focus`, which four contact fields and both multiselect
   * templates declare and nothing in this port honoured.
   *
   * ⚠️ TOGETHER THEY MEAN "OPEN THE WHOLE LIST WHEN THE FIELD IS CLICKED", and the field
   * they matter most to is Previous engagement, whose six values nobody can guess: production
   * lists all six on focus, this listed nothing until something was typed — which reads as an
   * autocomplete that does not work rather than one waiting for input.
   *
   * They are separate attributes upstream and stay separate here: `loadOnEmpty` lets an empty
   * query reach the loader at all, `loadOnFocus` is what asks for one when focus arrives.
   */
  loadOnEmpty?: boolean
  loadOnFocus?: boolean
  /**
   * Which of upstream's three tag templates to draw.
   *
   * ⚠️ A CHOSEN CATEGORY IS A DIFFERENT COLOUR, AND THAT IS WHAT THIS IS FOR. The two position
   * templates put `tag-is-title-class` on the `<li>` when the tag is a title CLASS rather than
   * a title, and `_search.page.scss:104` paints it amber (#FCECCC) where an ordinary tag is
   * blue (#E3F7FF). Rendering every tag with the generic template made a category
   * indistinguishable from a title — measured on both builders: production's chosen
   * "Engineer" came back `tag-item tag-is-title-class` on #FCECCC, ours `tag-item` on #E3F7FF.
   *
   * `position-ux` additionally wraps the contents in `tag-item-inner` and marks the host
   * `host-title-class-ux`, which the non-UX template does not — so this is an enum and not a
   * boolean.
   *
   * ⚠️ `location` IS A FOURTH, AND IT IS THE ONLY ONE THAT CHANGES THE TEXT.
   * `location-tags-input.tpl.html` ignores `display-property` and composes the label from the
   * tag itself — `(city ? city + ', ' : '') + region_state + ", " + country` — so "Dallas"
   * reads "Dallas, Texas, United States" there and read "Dallas" here. One field in the
   * candidate builder uses it (the cities / regions list under a non-Israel country) and two
   * in the contact builder; every other location field is the generic template with
   * `display-property="city"`, which is why only this one diverged.
   *
   * The comma before the country is unconditional upstream, so a tag with no `region_state`
   * renders ", United States". Ported as it stands: the label is the thing being matched.
   *
   * The `titles-amount` span is rendered by both position templates and hidden by the
   * stylesheet in the UX scope. It is drawn anyway: the rule that hides it is scoped, the
   * other scope shows it at 11px bold, and a template that omits the element cannot be
   * un-hidden by CSS.
   */
  tagTemplate?: 'generic' | 'position' | 'position-ux' | 'location'
  /** `debounce-delay`. */
  debounceDelay?: number
  /** `max-results-to-show`. */
  maxResults?: number
  /**
   * An alternative suggestion panel.
   *
   * ⚠️ THE TITLE FIELDS HAVE THEIR OWN TEMPLATE AND IT IS NOT A LIST. `typeahead-template-url`
   * points them at `titles-with-classes.title-class-ux.typeahead.tpl.html`, which splits the
   * SAME items into "Extended Categories" (`titleClass: true`) and "Specific Titles"
   * (everything else, plus the add-new row) under two labelled headers. Rendering the flat
   * list for them loses the grouping, the headers and the amber class pills — which is what
   * "not according to design" meant.
   */
  renderSuggestions?: (view: SuggestionPanelView) => ReactNode
  onAdd?: (tag: Tag) => void
  onRemove?: (index: number) => void
  children?: ReactNode
}

/**
 * `scope.highlight` (`ng-tags-input.js:690`) — the matched run wrapped in `<em>`.
 *
 * ⚠️ IT IS EVERY OCCURRENCE, NOT THE FIRST, and the match is case-INSENSITIVE while the text
 * shown is the suggestion's own casing. Upstream loops a global regex and rebuilds the string;
 * this returns nodes instead, so nothing in this package needs `dangerouslySetInnerHTML` to
 * render a list that comes from the server.
 */
function highlightMatch(text: string, query: string): ReactNode {
  if (query === '') return text
  const needle = query.toLowerCase()
  const haystack = text.toLowerCase()
  const parts: ReactNode[] = []
  let at = 0
  let found = haystack.indexOf(needle)
  while (found !== -1) {
    if (found > at) parts.push(text.slice(at, found))
    parts.push(<em key={`${String(found)}`}>{text.slice(found, found + query.length)}</em>)
    at = found + query.length
    found = haystack.indexOf(needle, at)
  }
  if (at < text.length) parts.push(text.slice(at))
  return parts
}

/** `tagsInputConfig.autosizeThreshold` — 3px, added to the measured text width. */
const AUTOSIZE_THRESHOLD = 3

/**
 * `location-tags-input.tpl.html`'s label expression, verbatim:
 * `{{(tag.city ? tag.city + ', ' : '') + tag.region_state + ", " + tag.country}}`.
 *
 * The fields live on the tag OBJECT upstream, which is the suggestion the typeahead returned;
 * here that object is `tag.source`. A tag that has none of them — a free-text entry this field
 * does not allow, or a model loaded before the port kept the source — falls back to its text
 * rather than rendering a bare comma.
 */
function locationLabel(tag: Tag): string {
  const field = (name: string): string => {
    const value = tag.source?.[name]
    return typeof value === 'string' ? value : ''
  }
  const city = field('city')
  const region = field('region_state')
  const country = field('country')
  if (city === '' && region === '' && country === '') return tag.text
  return `${city === '' ? '' : `${city}, `}${region}, ${country}`
}

export function TagsInput({
  tags,
  placeholder,
  name,
  className,
  indicator = false,
  indicatorClassName,
  suggestions = [],
  freeText = false,
  onQueryChange,
  loadSuggestions,
  minLength = 1,
  loadOnEmpty = false,
  loadOnFocus = false,
  tagTemplate = 'generic',
  debounceDelay = 500,
  maxResults = 20,
  renderSuggestions,
  onAdd,
  onRemove,
  children,
}: TagsInputProps): ReactElement {
  const [query, setQuery] = useState('')
  const [hasFocus, setHasFocus] = useState(false)

  // ⚠️ `ti-autosize` — THE INPUT IS SIZED TO ITS TEXT, AND WITHOUT IT THE FIELD WRAPS. The
  // vendor directive measures the value (or, when empty, the PLACEHOLDER) in a hidden
  // `<span class="input">` with `white-space: pre` and sets the input's width to that plus a
  // 3px threshold. Every tag and the input are floats inside a fixed-width box, so an input
  // left at its CSS width is too wide to sit beside a tag: it drops to the next line and the
  // row grows ~30px. Invisible while a field is empty, which is why it survived nine sections
  // of them and showed up on the automation screen's saved job filters.
  const measure = useRef<HTMLSpanElement>(null)
  const field = useRef<HTMLInputElement>(null)

  /**
   * `suggestionList.load` — debounced, and only once the query is long enough.
   *
   * ⚠️ THE LIST CLOSES ON A SHORT QUERY RATHER THAN KEEPING THE LAST ANSWER. The directive
   * resets the list when `shouldLoadSuggestions` is false, so deleting back to one character
   * hides the panel instead of leaving stale rows under a query that no longer produced them.
   */
  const [loaded, setLoaded] = useState<readonly Tag[]>([])
  const requestSeq = useRef(0)
  useEffect(() => {
    if (loadSuggestions === undefined) return undefined
    // ⚠️ `loadOnEmpty` ONLY EXCUSES THE EMPTY QUERY, not a short one. The directive's test is
    // `(loadOnEmpty && query.length === 0) || query.length >= minLength`, so with
    // `min-length="1"` and one character typed nothing changes — it is the zero case that
    // goes from "closed" to "the whole list". `loadOnFocus` is what asks for that list, so a
    // field declaring only `loadOnEmpty` still waits for the user to arrive.
    const shouldLoad =
      query.length >= minLength ||
      (query.length === 0 && loadOnEmpty && (!loadOnFocus || hasFocus))
    if (!shouldLoad) {
      setLoaded([])
      return undefined
    }
    const seq = requestSeq.current + 1
    requestSeq.current = seq
    const timer = setTimeout(() => {
      void loadSuggestions(query).then(
        (found) => {
          // A slow answer must not overwrite a newer one — the directive guards the same way.
          if (requestSeq.current === seq) setLoaded(found.slice(0, maxResults))
        },
        () => {
          if (requestSeq.current === seq) setLoaded([])
        },
      )
    }, debounceDelay)
    return () => {
      clearTimeout(timer)
    }
  }, [query, minLength, debounceDelay, maxResults, loadSuggestions, loadOnEmpty, loadOnFocus, hasFocus])

  // The caller's static list still works; a loader simply supplies one.
  const offered = loadSuggestions === undefined ? suggestions : loaded
  const [highlighted, setHighlighted] = useState(0)
  useEffect(() => {
    setHighlighted(0)
  }, [offered])
  const panelOpen = hasFocus && offered.length > 0

  const take = (tag: Tag): void => {
    onAdd?.(tag)
    setQuery('')
    onQueryChange?.('')
    setLoaded([])
  }
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined)
  useLayoutEffect(() => {
    const node = measure.current
    if (node === null) return
    const width = node.offsetWidth
    setInputWidth(width > 0 ? width + AUTOSIZE_THRESHOLD : undefined)
  }, [query, placeholder])

  // ⚠️ `<tags-input>` IS THE ELEMENT EVERY VENDOR RULE IS SCOPED UNDER. The whole of
  // `vendor/ng-tags-input/_ng-tags-input.scss` is written as `tags-input .host`,
  // `tags-input .tags .tag-item`, and so on — 133 lines, none of which can match a bare
  // `<div class="host">`. The caller's own class goes on the ELEMENT (upstream writes
  // `<tags-input class="tags-input-default-width or-in-between">`), not on `.host`, because
  // that is the width and the "or" separator between tags.
  return createElement(
    'tags-input',
    {
      ...(className === undefined ? {} : { className }),
      // ⚠️ `name` BELONGS ON THE ELEMENT, NOT THE INPUT. Upstream writes
      // `<tags-input name="industries">`; `ti-bind-attrs` binds only type, placeholder and
      // tabindex onto the inner field. The form addresses its validation messages by this
      // name (`ctrl.form.industries.$error.leftoverText`).
      ...(name === undefined ? {} : { name }),
    },
    // ⚠️ A CLICK ANYWHERE IN THE HOST FOCUSES THE INPUT, and without it the field has a dead
    // zone. The vendor directive is one line —
    // `element.find('div').on('click', () => input[0].focus())`
    // (`ng-tags-input.js:462`) — and it matters because the input is AUTOSIZED to its own
    // text: an empty field is only as wide as its placeholder, so most of the row is `.tags`
    // and clicking there did nothing. Reported as "clicking on the right side of the field
    // does not activate it", on every tag field in both builders.
    <div
      className={tagTemplate === 'position-ux' ? 'host host-title-class-ux' : 'host'}
      tabIndex={-1}
      onClick={() => {
        field.current?.focus()
      }}
    >
      <div className={`tags${hasFocus ? ' focused' : ''}`}>
        {/* ⚠️ `tags-input-indicator` IS THE CLASS THAT MATTERS, AND IT GOES FIRST. The
            directive PREPENDS `<i class="tags-input-indicator {indicatorClass}">` to `.tags`,
            and that class is `position: absolute` — six lines in
            `common/directives/tags-input-utils/indicator/`. Rendering only the caller's icon
            class, appended, leaves the magnifier in the flow: every tags-input is then 20px
            taller than production, which is invisible on one field and adds up over nine
            sections. */}
        {indicator && tags.length === 0 ? (
          <i className={['tags-input-indicator', indicatorClassName].filter(Boolean).join(' ')} />
        ) : null}
        <ul className="tag-list">
          {tags.map((tag, index) => {
            // `ng-class="{'tag-is-title-class': tag.titleClass}"` — only the two position
            // templates carry it, and the flag lives on the row the typeahead answered with.
            const isTitleClass = tagTemplate !== 'generic' && tag.source?.['titleClass'] === true
            const amount = typeof tag.source?.['titlesAmount'] === 'number' ? tag.source['titlesAmount'] : 1
            const body = (
              <>
                {/* `<flame>` is a component element of upstream's, and the inline style is
                    upstream's too — it nudges the marker up out of the line box.
                    `createElement` because JSX has no declaration for a hyphen-less unknown
                    tag. The generic template is the only one that draws it. */}
                {(tagTemplate === 'generic' || tagTemplate === 'location') && tag.inDemand === true
                  ? createElement('flame', { style: { position: 'relative', top: '-8px' } })
                  : null}
                <span>{tagTemplate === 'location' ? locationLabel(tag) : tag.text}</span>
                {isTitleClass ? (
                  <span className="titles-amount">
                    {` (${String(amount)} title${amount === 1 ? '' : 's'})`}
                  </span>
                ) : null}
                {/* `options.removeTagSymbol` — the vendor's `×`, as TEXT. */}
                <a
                  className="remove-button"
                  href=""
                  onClick={(event) => {
                    event.preventDefault()
                    onRemove?.(index)
                  }}
                >
                  {'×'}
                </a>
              </>
            )
            return (
              <li className={isTitleClass ? 'tag-item tag-is-title-class' : 'tag-item'} key={tag.text}>
                {tagTemplate === 'position-ux' ? <span className="tag-item-inner">{body}</span> : body}
              </li>
            )
          })}
        </ul>
        {/* The hidden twin the width is measured from. `class="input"` so it inherits the
            same font metrics as the field it sizes. */}
        <span
          ref={measure}
          className="input"
          style={{ visibility: 'hidden', position: 'absolute', width: 'auto', whiteSpace: 'pre' }}
          aria-hidden
        >
          {query.length === 0 ? placeholder : query}
        </span>
        <input
          ref={field}
          autoComplete="chrome-off"
          className="input"
          style={inputWidth === undefined ? undefined : { width: `${String(inputWidth)}px` }}
          placeholder={placeholder}
          value={query}
          onFocus={() => setHasFocus(true)}
          onBlur={() => {
            setHasFocus(false)
            // ⚠️ `addOnBlur` IS TRUE BY DEFAULT, and this port had no equivalent — so a
            // company name typed and then clicked away from stayed as raw text in the box
            // while production had already turned it into a tag. The field then counted as
            // unused ("Filters used 2/9" against production's 3/9) and saved nothing.
            if (!freeText) return
            const text = query.trim()
            if (text === '') return
            take({ text })
          }}
          onChange={(event) => {
            setQuery(event.target.value)
            onQueryChange?.(event.target.value)
          }}
          onKeyDown={(event) => {
            // ⚠️ ENTER DOES NOT ADD THE TYPED TEXT, unless the field is free-text.
            // `add-from-autocomplete-only` means a tag comes from a suggestion or not at all;
            // the first suggestion is auto-selected (`auto-select-first-suggestion`), so Enter
            // takes THAT rather than the query. Keywords and company names are the exceptions.
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              if (offered.length === 0) return
              event.preventDefault()
              setHighlighted((at) =>
                event.key === 'ArrowDown'
                  ? (at + 1) % offered.length
                  : (at - 1 + offered.length) % offered.length,
              )
              return
            }
            if (event.key === 'Escape') {
              setLoaded([])
              return
            }
            // ⚠️ COMMA COMMITS TOO, AND ONLY ON A FREE-TEXT FIELD. `ng-tags-input.js:247-250`
            // defaults are `addOnEnter: true, addOnComma: true, addOnBlur: true, addOnSpace:
            // false` — and line 130 says all four are ignored when `addFromAutocompleteOnly`
            // is set, which is every other field in the builder.
            if (event.key !== 'Enter' && !(freeText && event.key === ',')) return
            event.preventDefault()
            // `auto-select-first-suggestion` — the highlighted row wins, and on a free-text
            // field the typed words are the fallback when nothing is offered.
            const picked = event.key === ',' ? undefined : offered[highlighted]
            const tag = picked ?? (freeText ? (query.trim() === '' ? undefined : { text: query.trim() }) : undefined)
            if (tag === undefined) return
            take(tag)
          }}
        />
      </div>
      {/* `<div class="autocomplete" ng-show="suggestionList.visible">` — appended inside the
          host by `ti-transclude-append`, so it is a SIBLING of `.tags`. The vendor stylesheet
          positions it absolutely against the host, which is why it must not be nested in
          `.tags`: inside, it inherits that element's overflow and clips at one row. */}
      {panelOpen && renderSuggestions !== undefined
        ? renderSuggestions({
            items: offered,
            highlighted,
            query,
            onPick: take,
            onHighlight: setHighlighted,
            highlight: (text) => highlightMatch(text, query),
          })
        : null}
      {panelOpen && renderSuggestions === undefined ? (
        <div className="autocomplete">
          <ul className="suggestion-list">
            {offered.map((tag, index) => (
              <li
                // ⚠️ `skills-typeahead` IS ON EVERY ROW OF THIS TEMPLATE, and the add-new row
                // carries `isolated-item` too. The builder's flat fields use
                // `autocomplete-sans-freetext-prefix.typeahead.tpl.html`, not the vendor's
                // bare one, and both classes are style hooks: the first sizes the row, the
                // second is the rule that visually separates "your own words" from the real
                // suggestions. Prod's add-new row is also QUOTED — which is how a user tells
                // the two apart, and the half of this that was reported.
                className={[
                  'suggestion-item',
                  'skills-typeahead',
                  index === highlighted ? 'selected' : '',
                  tag.addNew === true && offered.length > 1
                    ? `isolated-item ${index === 0 ? 'isolated-item-first' : ''}${
                        index === offered.length - 1 ? 'isolated-item-last' : ''
                      }`.trim()
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                key={`${tag.text}-${String(index)}`}
                // `ng-mousedown` rather than click: the input's blur fires first otherwise and
                // the panel is gone before the click lands.
                onMouseDown={(event) => {
                  event.preventDefault()
                  take(tag)
                }}
                onMouseEnter={() => setHighlighted(index)}
              >
                {tag.addNew === true ? (
                  <span>
                    {'"'}
                    {highlightMatch(tag.text, query)}
                    {'"'}
                  </span>
                ) : (
                  <span>{highlightMatch(tag.text, query)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {children}
    </div>,
  )
}
